/**
 * Learning-domain catalog client.
 *
 * The catalog lives canonically in the backend (single source of
 * truth) and is fetched from ``GET /api/coaching/domain-catalog``.
 * The backend's Python pipeline processes the JSON, validates icon
 * paths, and inlines SVG markup before responding, so the frontend's
 * only contract is the shape returned by that endpoint.
 *
 * Why a module-level promise cache?  The catalog is small (~5 KB),
 * static for the session, and consumed by several components in the
 * arc-generation flow.  A single fetch — shared across all callers
 * via a memoized promise — keeps the network surface predictable
 * and avoids waterfall re-fetches on render.
 */

import { useEffect, useState } from "react";

export interface Domain {
  domainId: string;
  name: string;
  promise: string;
  whyItMatters: string;
  skills: string[];
  concepts: string[];
  exampleQuests: string[];
  defaultEtaSessions: number;
  iconSvg: string;
}

export interface DomainCategory {
  categoryId: string;
  categoryName: string;
  domains: Domain[];
}

export interface DomainCatalog {
  schemaVersion: number;
  categories: DomainCategory[];
}

// ── In-memory cache ─────────────────────────────────────────────────

let _catalogPromise: Promise<DomainCatalog> | null = null;

/** Fetch the catalog from the backend, memoized for the page life-
 * time.  Safe to call from many places — first call hits the
 * network, every subsequent call reuses the same promise. */
export function fetchDomainCatalog(): Promise<DomainCatalog> {
  if (_catalogPromise == null) {
    _catalogPromise = fetch("/api/coaching/domain-catalog", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(async (res) => {
      if (!res.ok) {
        // Reset the cache on failure so a retry can try again
        // instead of returning the rejected promise forever.
        _catalogPromise = null;
        throw new Error(
          `domain-catalog fetch failed: ${res.status} ${res.statusText}`
        );
      }
      return (await res.json()) as DomainCatalog;
    });
  }
  return _catalogPromise;
}

/** Reset the cache.  Exported only for tests; production code
 * should never need this. */
export function _resetDomainCatalogCache(): void {
  _catalogPromise = null;
}

// ── React hook ──────────────────────────────────────────────────────

export interface UseDomainCatalogResult {
  catalog: DomainCatalog | null;
  domains: Record<string, Domain>;
  loading: boolean;
  error: Error | null;
}

const EMPTY_DOMAINS: Record<string, Domain> = {};

/** Component-friendly accessor: returns the catalog (plus a flat
 * ``domainId → Domain`` lookup) once the fetch resolves.  Renders
 * with ``loading: true`` and ``catalog: null`` on first paint, then
 * re-renders with the resolved data. */
export function useDomainCatalog(): UseDomainCatalogResult {
  const [catalog, setCatalog] = useState<DomainCatalog | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDomainCatalog().then(
      (c) => { if (!cancelled) setCatalog(c); },
      (e) => { if (!cancelled) setError(e); },
    );
    return () => { cancelled = true; };
  }, []);

  const domains = catalog == null
    ? EMPTY_DOMAINS
    : Object.fromEntries(
        catalog.categories.flatMap((cat) =>
          cat.domains.map((d) => [d.domainId, d])
        )
      );

  return {
    catalog,
    domains,
    loading: catalog == null && error == null,
    error,
  };
}
